import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Loader2, PenLine, Ruler, Undo2, Trash2, Wand2, Save, Download } from 'lucide-react';
import { CATEGORIAS, VALORES_DE_CATEGORIA, dec, medirLesion, num } from '../../lib/oftalmo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const urlImagen = (pid, id, tam) => `${BACKEND_URL}/api/patients/${pid}/fotos/${id}/img${tam ? `?tam=${tam}` : ''}`;

const REFERENCIAS = [
  { que: 'Diámetro corneal horizontal (HVID 11,7 mm)', mm: 11.7 },
  { que: 'Diámetro vertical de la papila (1,8 mm)', mm: 1.8 },
  { que: 'Diámetro de la papila (1,5 mm)', mm: 1.5 },
  { que: 'Escala de la imagen (a medida)', mm: 1 },
];

/** Un contorno y una referencia sobre la imagen, en % (viewBox 0-100). */
export function CapaMedicion({ medicion, color = '#FACC15', cerrado = true, grosor = 0.35, mostrarRef = true }) {
  if (!medicion) return null;
  const pts = (medicion.puntos || []).map(([x, y]) => `${x},${y}`).join(' ');
  const ref = medicion.referencia;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
      {(medicion.puntos || []).length > 1 && (cerrado
        ? <polygon points={pts} fill={`${color}33`} stroke={color} strokeWidth={grosor} vectorEffect="non-scaling-stroke" style={{ strokeWidth: 2 }} />
        : <polyline points={pts} fill="none" stroke={color} vectorEffect="non-scaling-stroke" style={{ strokeWidth: 2 }} />)}
      {(medicion.puntos || []).map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={0.45} fill={color} />
      ))}
      {mostrarRef && ref?.a && ref?.b && (
        <line x1={ref.a[0]} y1={ref.a[1]} x2={ref.b[0]} y2={ref.b[1]} stroke="#22D3EE"
          strokeDasharray="1.2 0.8" vectorEffect="non-scaling-stroke" style={{ strokeWidth: 2 }} />
      )}
    </svg>
  );
}

export default function VisorImagen({ pid, imagen, open, onOpenChange, puedeEditar, onGuardada, onBorrada }) {
  const [f, setF] = useState(null);
  const [modo, setModo] = useState(null); // 'contorno' | 'referencia'
  const [guardando, setGuardando] = useState(false);
  const [detectando, setDetectando] = useState(false);
  const caja = useRef(null);

  useEffect(() => {
    if (imagen) {
      setF({
        categoria: imagen.categoria, ojo: imagen.ojo, fecha: imagen.fecha, diagnostico: imagen.diagnostico,
        notas: imagen.notas, valoracion: imagen.valoracion, valores: { ...(imagen.valores || {}) },
        medicion: imagen.medicion ? JSON.parse(JSON.stringify(imagen.medicion)) : null,
      });
      setModo(null);
    }
  }, [imagen]);
  if (!imagen || !f) return null;

  const medicion = f.medicion || { puntos: [], referencia: null, que: '' };
  const medida = medirLesion(medicion, imagen.ancho, imagen.alto);
  const setMed = (m) => setF((p) => ({ ...p, medicion: { ...medicion, ...m } }));

  const clic = (e) => {
    if (!modo || !caja.current) return;
    const r = caja.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 100000) / 1000;
    const y = Math.round(((e.clientY - r.top) / r.height) * 100000) / 1000;
    if (modo === 'contorno') setMed({ puntos: [...(medicion.puntos || []), [x, y]], automatica: false });
    if (modo === 'referencia') {
      const ref = medicion.referencia || { mm: 11.7, que: REFERENCIAS[0].que };
      if (!ref.a || ref.b) setMed({ referencia: { ...ref, a: [x, y], b: null } });
      else { setMed({ referencia: { ...ref, b: [x, y] } }); setModo(null); }
    }
  };

  const detectar = async () => {
    setDetectando(true);
    try {
      const r = await api.post(`/api/patients/${pid}/oftalmologia/imagenes/${imagen.id}/segmentar`);
      if (!r.data.encontrada) toast.info('No se ve zona teñida de verde (fluoresceína) en esta imagen');
      else {
        setMed({ puntos: r.data.puntos, automatica: true, que: medicion.que || 'Defecto epitelial (fluoresceína)' });
        if (!medicion.referencia) toast.info('Marca la referencia (por ejemplo el diámetro corneal) para tener el área en mm²');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo analizar la imagen');
    } finally {
      setDetectando(false);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const med = (medicion.puntos || []).length >= 3 || medicion.referencia?.b ? medicion : null;
      const r = await api.put(`/api/patients/${pid}/oftalmologia/imagenes/${imagen.id}`, { ...f, medicion: med });
      toast.success('Imagen guardada');
      onGuardada?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    if (!window.confirm('¿Borrar esta imagen de la historia? No se puede deshacer.')) return;
    try {
      await api.delete(`/api/patients/${pid}/oftalmologia/imagenes/${imagen.id}`);
      toast.success('Imagen borrada');
      onBorrada?.(imagen.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const valoresCat = VALORES_DE_CATEGORIA[f.categoria] || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(1200px,96vw)] p-0 overflow-hidden" data-testid="oft-visor">
        <DialogTitle className="sr-only">Imagen</DialogTitle>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] max-h-[92vh]">
          <div className="bg-black flex items-center justify-center p-2 min-h-[300px] overflow-auto">
            <div ref={caja} className={`relative inline-block ${modo ? 'cursor-crosshair' : ''}`} onClick={clic}
              data-testid="oft-visor-imagen">
              <img src={urlImagen(pid, imagen.id)} alt="" className="block max-h-[82vh] max-w-full select-none" draggable={false} />
              <CapaMedicion medicion={medicion} cerrado={modo !== 'contorno'} />
            </div>
          </div>
          <div className="p-4 space-y-3 overflow-y-auto max-h-[92vh] text-sm">
            <div className="font-display font-semibold text-base">{CATEGORIAS[f.categoria] || 'Imagen'} {f.ojo && `· ${f.ojo}`}</div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Prueba
                <select value={f.categoria} disabled={!puedeEditar} onChange={(e) => setF({ ...f, categoria: e.target.value })}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                  {Object.entries(CATEGORIAS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
                </select>
              </label>
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Ojo
                <select value={f.ojo} disabled={!puedeEditar} onChange={(e) => setF({ ...f, ojo: e.target.value })}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                  <option value="">—</option><option value="OD">OD</option><option value="OI">OI</option>
                </select>
              </label>
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Fecha
                <Input type="date" value={f.fecha || ''} disabled={!puedeEditar} onChange={(e) => setF({ ...f, fecha: e.target.value })} className="h-9" />
              </label>
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Diagnóstico
                <Input value={f.diagnostico || ''} disabled={!puedeEditar} onChange={(e) => setF({ ...f, diagnostico: e.target.value })} className="h-9" />
              </label>
              {valoresCat.map(([k, t]) => (
                <label key={k} className="text-xs text-muted-foreground flex flex-col gap-1">{t}
                  <Input inputMode="decimal" value={f.valores?.[k] ?? ''} disabled={!puedeEditar} data-testid={`oft-valor-${k}`}
                    onChange={(e) => setF({ ...f, valores: { ...f.valores, [k]: e.target.value } })} className="h-9" />
                </label>
              ))}
            </div>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Valoración
              <Textarea rows={3} value={f.valoracion || ''} disabled={!puedeEditar} data-testid="oft-valoracion"
                onChange={(e) => setF({ ...f, valoracion: e.target.value })} placeholder="Lo que se ve y lo que se decide" />
            </label>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Notas
              <Textarea rows={2} value={f.notas || ''} disabled={!puedeEditar} onChange={(e) => setF({ ...f, notas: e.target.value })} />
            </label>

            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm flex items-center gap-1"><Ruler className="w-4 h-4" /> Medir lesión</div>
                {medida.area_mm2 !== null && (
                  <div className="text-sm" data-testid="oft-area"><b>{dec(medida.area_mm2, 2)} mm²</b> · Ø {dec(medida.diametro_mm, 2)} mm</div>
                )}
              </div>
              {puedeEditar && (
                <>
                  <Input placeholder="Qué se mide (úlcera, atrofia, hemorragia…)" value={medicion.que || ''}
                    onChange={(e) => setMed({ que: e.target.value })} className="h-8 text-xs" />
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" variant={modo === 'contorno' ? 'default' : 'outline'} data-testid="oft-dibujar"
                      onClick={() => setModo(modo === 'contorno' ? null : 'contorno')}>
                      <PenLine className="w-3.5 h-3.5 mr-1" />{modo === 'contorno' ? 'Terminar contorno' : 'Dibujar contorno'}
                    </Button>
                    <Button size="sm" variant="outline" disabled={!(medicion.puntos || []).length}
                      onClick={() => setMed({ puntos: medicion.puntos.slice(0, -1) })}><Undo2 className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" disabled={!(medicion.puntos || []).length}
                      onClick={() => setMed({ puntos: [] })}><Trash2 className="w-3.5 h-3.5" /></Button>
                    {['cornea', 'segmento_anterior'].includes(f.categoria) && (
                      <Button size="sm" variant="outline" onClick={detectar} disabled={detectando} data-testid="oft-detectar-tincion">
                        {detectando ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                        Detectar tinción
                      </Button>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {modo === 'contorno' && 'Haz clic alrededor del borde de la lesión; se cierra solo.'}
                    {modo === 'referencia' && (medicion.referencia?.a && !medicion.referencia?.b
                      ? 'Ahora el otro extremo de la referencia.' : 'Haz clic en un extremo de la referencia.')}
                    {!modo && 'Para el área en mm², marca una referencia de tamaño conocido.'}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <select className="h-8 rounded-md border border-input bg-background px-2 text-xs flex-1 min-w-[180px]"
                      value={REFERENCIAS.findIndex((r) => r.que === medicion.referencia?.que)}
                      onChange={(e) => {
                        const r = REFERENCIAS[Number(e.target.value)] || REFERENCIAS[0];
                        setMed({ referencia: { ...(medicion.referencia || {}), que: r.que, mm: r.mm } });
                      }}>
                      {REFERENCIAS.map((r, i) => <option key={r.que} value={i}>{r.que}</option>)}
                    </select>
                    <Input className="h-8 w-20 text-xs" inputMode="decimal" value={medicion.referencia?.mm ?? ''}
                      onChange={(e) => setMed({ referencia: { ...(medicion.referencia || {}), mm: num(e.target.value) } })} />
                    <span className="text-xs">mm</span>
                    <Button size="sm" variant={modo === 'referencia' ? 'default' : 'outline'}
                      onClick={() => { setMed({ referencia: { ...(medicion.referencia || { mm: 11.7, que: REFERENCIAS[0].que }), a: null, b: null } }); setModo('referencia'); }}>
                      Marcar referencia
                    </Button>
                  </div>
                </>
              )}
              {medida.area_mm2 === null && medida.area_pct !== null && (
                <div className="text-xs text-muted-foreground">Área: {dec(medida.area_pct, 2)} % de la imagen (sin referencia).</div>
              )}
            </div>

            {(imagen.informes_ia || []).length > 0 && (
              <div className="rounded-lg border p-3 text-xs space-y-2">
                <div className="font-semibold text-sm">Lecturas con IA</div>
                {imagen.informes_ia.map((i) => (
                  <div key={i.id} className="whitespace-pre-wrap">{i.texto}<div className="text-muted-foreground mt-1">{i.por} · {String(i.fecha).slice(0, 10)}</div></div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {puedeEditar && <Button onClick={guardar} disabled={guardando} data-testid="oft-guardar-imagen">
                {guardando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Guardar</Button>}
              <Button variant="outline" asChild><a href={urlImagen(pid, imagen.id)} download target="_blank" rel="noreferrer"><Download className="w-4 h-4 mr-1" />Descargar</a></Button>
              {puedeEditar && <Button variant="ghost" className="text-red-600 ml-auto" onClick={borrar}><Trash2 className="w-4 h-4" /></Button>}
            </div>
            <div className="text-[11px] text-muted-foreground">Subida por {imagen.uploaded_by_name || '—'}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
